// ─── What a container needs before its driver may set off, and what freezes ──
// once it does — the same six fields, seen from either end.
//
// The dispatcher and the driver both write to these fields — dispatch from the
// booking, the driver from in front of the box — and the first step is refused
// until every one of them is there. The server holds the same two lists
// (services/containerOrderService.js) and is the actual gate; this copy exists
// so the forms can mark fields required, disable the frozen ones, and say what
// is still outstanding, rather than only finding out when a save is rejected.

import { STARTED_LINE_STATUSES } from "../types/containerOrders";
import { completedStepCount } from "./containerLineSteps";
import type { ContainerOrderLine } from "../types/containerOrders";

/**
 * ── The two gates a container passes through ─────────────────────────────────
 *
 * There used to be one, checked before the driver's very first step, and it
 * asked for six things at a moment when only two of them could honestly be
 * known. The other four describe a physical box — its number, its plated tare,
 * its payload rating, what is going inside it — and on the commonest route of
 * all the driver is being sent to a terminal *to collect that box*. Demanding
 * them beforehand meant dispatch typing a figure they had not read.
 *
 * So the gate is split at the place the facts arrive: the first stop.
 *
 *   arrival     the truck          dispatch's own, known when the job is
 *                                  planned. Checked before step 0.
 *   departure   the box's figures  read off the container in front of them.
 *                                  Checked before step 1.
 *
 * "The first stop" is whichever stop that is — a route need not begin at a
 * terminal — so neither list is tied to a role. Both are indexed off the step
 * log, the one thing every route has in the same shape.
 *
 * The seal is on neither, deliberately: it is fitted when the box is closed,
 * which is after both gates, and its number often reaches the office later
 * still. A container that has not been sealed yet is still one that can
 * legitimately set off.
 */
/**
 * One entry, and it is the truck.
 *
 * There were two, and the driver was the other. It came off when containers
 * stopped being dispatched to people: a driver takes whichever truck the yard
 * gives them and signs in on the screen in its cab with a PIN, so the container's
 * route to a human being runs through the vehicle. Requiring a named driver at
 * planning time asked dispatch to predict a rota, and a container assigned to a
 * driver who called in sick was invisible to whoever actually had the truck.
 *
 * `driverId` still exists on a container and is still shown — but as a record of
 * who took it, written by the step recorder, never as a field the form sets.
 */
export const LINE_ARRIVAL_REQUIREMENTS = ["vehicle"] as const;

/**
 * What the driver must have before they may leave the first stop.
 *
 * `cargo` is the packing list, and it stands where the VGM used to. The VGM is
 * no longer a requirement because it is no longer a field — it is derived from
 * the tare and the packing list — but the hole that would leave has to be
 * closed: an empty list makes the sum come out at exactly the tare, which is a
 * plausible-looking gross mass for a box about to be loaded and which nothing
 * downstream could tell from a real one.
 *
 * It used to be the one entry the driver could not supply. It is not any more —
 * the packing list is a table on their own screen now — see
 * DRIVER_FILLABLE_REQUIREMENTS.
 */
export const LINE_DEPARTURE_REQUIREMENTS = [
  "containerNumber",
  // The tare belongs with the payload rating: every rule relating the VGM, the
  // payload and the packing list is stated against it, so a container without
  // one is a container none of them can be checked on.
  "tareKg",
  "maxPayloadKg",
  "cargo",
] as const;

/**
 * Both tiers as one list, in the order they come due.
 *
 * For the readers that ask "is this container fully described yet" and do not
 * care which gate a field belongs to — the office's edit form, which marks
 * every box required at once because a dispatcher fills them in one sitting.
 */
export const LINE_START_REQUIREMENTS = [
  ...LINE_ARRIVAL_REQUIREMENTS,
  ...LINE_DEPARTURE_REQUIREMENTS,
] as const;

export type LineStartRequirement = (typeof LINE_START_REQUIREMENTS)[number];
export type LineArrivalRequirement = (typeof LINE_ARRIVAL_REQUIREMENTS)[number];
export type LineDepartureRequirement =
  (typeof LINE_DEPARTURE_REQUIREMENTS)[number];

/**
 * A field the dispatcher's form can freeze.
 *
 * Wider than the start requirements, and it has to be: the max gross is a plate
 * rating that freezes exactly like the max payload beside it, but it is not
 * something a container must have before it may set off. Conflating "must be
 * present to start" with "cannot be changed once started" was only ever an
 * accident of the two lists having had the same members.
 */
export type LineLockableField = LineStartRequirement;

/**
 * The fields that settle the moment a container starts moving.
 *
 * What closes is the box's *identity*: its number was filed with the terminal
 * under that value, so changing it afterwards would leave the event log
 * describing a container nobody handled. Everything else — vessel, references,
 * cut-off, the route, the packing list, notes — stays editable throughout,
 * because none of it changes what is being driven.
 *
 * The seal is no longer one of them. It is optional, and it is the identifier
 * most likely to still be blank when the wheels turn, so freezing it did not
 * preserve a record — it made one impossible to enter. Dispatch can fill it in
 * whenever the number arrives.
 *
 * Which truck has it is not identity, and does not freeze. A run under way is
 * exactly when a truck breaks down or a box is moved onto another chassis, and
 * dispatch has to hand the container over; a frozen field there does not prevent
 * the reassignment, it only stops the record from admitting it. Editable is not
 * erasable, though — a started container may not be left on no truck at all,
 * which would take it off every driver's screen; see REQUIRED_ONCE_STARTED in
 * the line model.
 *
 * The driver does not appear here at all any more, because it is not a field the
 * form writes: whoever records a step becomes the container's driver.
 *
 * The two weights that describe the *load* are open for a different reason —
 * the tare and the VGM. Neither is a decision; they are figures read off a plate
 * and a weighbridge, and the weighing happens after loading, which is stop 2 of
 * 3. The packing list is finalised there too and stays editable for the same
 * reason, and all three are arithmetically coupled: freezing the weights at the
 * first step left a dead end where a corrected packing list could not be saved
 * and the fields that would resolve it were locked.
 *
 * The max payload used to be frozen alongside it, on the argument that a plate
 * rating is not an observation about this journey — it was stamped on the
 * container before the booking existed, so nothing on the road is a reason to
 * retype it. True, and the wrong conclusion: nothing on the road changes the
 * rating, but the road is where the rating is first read correctly. It is a
 * transcription, by dispatch from a booking or by a driver at the door, and a
 * transcription can be wrong at any point in the run.
 *
 * The driver's screen never froze it, so freezing it here only decided *who*
 * could fix a misread plate — and a dispatcher looking straight at the right
 * figure had no way to enter it. Every weight is now open to both hands for the
 * whole run; only the box's identity closes. Clearing one is still refused, so
 * what is allowed is correcting a figure, never removing it.
 *
 * Mirrors LINE_FIELDS_LOCKED_AFTER_START in services/containerOrderService.js,
 * which is the rule the API actually enforces.
 */
export const LINE_FIELDS_LOCKED_AFTER_START: readonly LineLockableField[] = [
  "containerNumber",
];

/**
 * The ones the driver can fill in from the yard, on their own screen.
 *
 * Everything that describes the box and what went into it — the tare included,
 * since it is stamped on the container standing in front of them. Only the
 * truck is dispatch's alone, for the obvious reason.
 *
 * The seal is on the driver's screen too, but not in this list: the list is
 * about *start requirements* the driver can clear, and the seal is no longer
 * one, so it can never be among the outstanding fields this answers for.
 *
 * `cargo` joined the list when the packing list did. It was excluded because
 * the only way to satisfy it from a cab was the declared load, and the rows
 * themselves were dispatch's — so a container held up for want of a packing
 * list was genuinely not the driver's to unblock. Both are theirs now, and the
 * table is on the same card as the gate that is refusing them.
 *
 * What this actually decides is one sentence in the stop frame: whether the
 * "still needed" notice adds "you can fill them in above". Getting it wrong is
 * not a locked screen, it is worse in its way — a driver reading a list of
 * missing fields with no suggestion that anything on the screen in front of
 * them would clear it, and ringing the office about a table they could have
 * typed into.
 */
export const DRIVER_FILLABLE_REQUIREMENTS: readonly LineStartRequirement[] = [
  "containerNumber",
  "tareKg",
  "maxPayloadKg",
  "cargo",
];

/**
 * The line field each requirement reads.
 *
 * `cargo` reads the packing list itself. The VGM is gone from this map because
 * it is gone from the requirements — it is derived from the two fields above
 * rather than being one of them.
 */
const FIELD_OF = {
  vehicle: "vehicleId",
  containerNumber: "containerNumber",
  tareKg: "tareKg",
  maxPayloadKg: "maxPayloadKg",
  cargo: "cargoItems",
} as const satisfies Record<LineStartRequirement, keyof ContainerOrderLine>;

/**
 * What the container has to say about its load, said either way.
 *
 * `cargo` is the one requirement that is not a field. It asks whether the
 * container states what is going inside, and there are two ways to answer: an
 * itemised packing list, or a weighed figure. A weighed load arrives as one
 * number on a bridge ticket and many bookings never itemise at all, so a gate
 * that only took rows stopped trucks over a format rather than over a fact —
 * and the VGM it protects is derived from whichever figure is in force.
 *
 * The mirror of `hasCargoStatement` in models/ContainerOrderLine.js. Zero is a
 * statement, not a blank: an empty repositioning move carries nothing.
 */
const hasCargoStatement = (
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
): boolean => {
  const declared = line.cargoWeightKg;
  if (typeof declared === "number" && Number.isFinite(declared) && declared >= 0) {
    return true;
  }
  return Array.isArray(line.cargoItems) && line.cargoItems.length > 0;
};

/**
 * The five fields the start gate reads.
 *
 * Exported so a caller assembling them by hand — the edit form, which holds
 * half-typed strings rather than a container — can be held to supplying all
 * five. Omitting one used to be silent, and silence here reads as "missing":
 * the tare was left out of the edit form's object and every container on the
 * page was therefore permanently short of its tare, whatever was typed in the
 * box. Anything derived from that answer inherited the lie.
 */
export type LineStartField = (typeof FIELD_OF)[LineStartRequirement];

/**
 * Whether one requirement is satisfied by a value.
 *
 * A reference may arrive as an id string or as a populated object, and either
 * one means the field is set; whitespace is not a booking reference, and zero
 * is not a weight — a box with no gross mass has not been weighed, it has been
 * left blank.
 */
const filled = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  // The packing list is the one requirement that is a list, and an empty one is
  // not an answer — it is the question still open. Left to the catch-all below
  // it would count as filled, which is the hole the packing list was put on the
  // departure gate to close.
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/**
 * Which requirements of a given tier this container is still short of.
 *
 * Every requirement is answered by its own field, except `cargo` — which asks a
 * question two different fields can answer, and so has a reader of its own.
 */
const missingOf = <T extends LineStartRequirement>(
  requirements: readonly T[],
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
): T[] =>
  requirements.filter((key) =>
    key === "cargo" ? !hasCargoStatement(line) : !filled(line[FIELD_OF[key]]),
  );

/**
 * What dispatch still owes before the driver may report in at the first stop.
 *
 * The truck, and it is not the driver's to supply — so a container failing this
 * is never something they can unblock from the yard. It is also not something
 * they would be looking at: work reaches a driver through the truck they signed
 * in on, so a container with no truck is on nobody's screen to be blocked by.
 */
export const missingArrivalRequirements = (
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
): LineArrivalRequirement[] => missingOf(LINE_ARRIVAL_REQUIREMENTS, line);

/** What is still missing before the driver may leave the first stop. */
export const missingDepartureRequirements = (
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
): LineDepartureRequirement[] => missingOf(LINE_DEPARTURE_REQUIREMENTS, line);

/** Everything outstanding across both gates. */
export function missingLineStartRequirements(
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
): LineStartRequirement[] {
  return missingOf(LINE_START_REQUIREMENTS, line);
}

/**
 * What this container is blocked on *right now*, given how far it has got.
 *
 * The one question the driver's screen actually asks. Before the first step it
 * is the arrival gate; between the first and the second it is the departure
 * gate; after that, nothing — a run under way is allowed to finish whatever
 * happens to the container behind the driver's back, which is the same latitude
 * the server gives it.
 */
export function blockingRequirements(
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
  stepsRecorded: number,
): LineStartRequirement[] {
  if (stepsRecorded === 0) return missingArrivalRequirements(line);
  if (stepsRecorded === 1) return missingDepartureRequirements(line);
  return [];
}

/**
 * Whether the driver is blocked at the gate they are currently standing at.
 *
 * Only meaningful at the first two steps — see `blockingRequirements`. Past
 * them the server lets a run finish regardless, so a container that started
 * under an older set of details is never stranded halfway.
 */
export function isLineBlockedFromStarting(
  line: Partial<Record<LineStartField, unknown>> & { cargoWeightKg?: unknown },
  stepsRecorded: number,
): boolean {
  return blockingRequirements(line, stepsRecorded).length > 0;
}

/**
 * Whether a truck has been out with this container at all.
 *
 * Membership rather than two equality checks: the run is reported step by step,
 * so "started" is five statuses plus `completed` and not a pair. The list is
 * derived from the same table the server derives its own from, so adding a step
 * cannot leave one side of this rule behind.
 *
 * Not the same question as `hasLeftFirstStop` below, and the two must not be
 * confused again — see the note there.
 */
export function hasStarted(line: Pick<ContainerOrderLine, "status">): boolean {
  return STARTED_LINE_STATUSES.includes(line.status);
}

/**
 * Whether the container has left its first stop — when the box's own figures
 * settle.
 *
 * This is the freeze point, and it is deliberately *not* `hasStarted`. The two
 * were the same test, and that was a deadlock:
 *
 *   the container number and the plated max payload froze on the first recorded
 *   step, which is the driver arriving at the first stop;
 *
 *   the same two are required to *leave* that stop, because that is the first
 *   moment anybody has stood in front of the door they are painted on.
 *
 * A container dispatched without a number — the ordinary case, since the driver
 * is being sent to collect a box nobody has seen — was therefore frozen by the
 * arrival and then demanded by the departure, with no save able to satisfy
 * both. The driver was told the number could no longer be changed while looking
 * at an empty field marked required.
 *
 * So everything about the box stays open for as long as the driver is standing
 * at it, and closes when they drive away. Mirrors `hasLeftFirstStop` in
 * services/containerOrderService.js, which is the rule the API enforces.
 */
export function hasLeftFirstStop(
  line: Parameters<typeof completedStepCount>[0],
): boolean {
  // Two steps, not one: a stop is two halves — arrive, then done.
  return completedStepCount(line) > 1;
}

/**
 * True for a field this container has already settled.
 *
 * A field with nothing in it is never frozen, whatever the container has done.
 * The freeze protects a *recorded* value — the number the terminal paperwork
 * was filed under — and a blank is not one; refusing to accept one would turn a
 * gap in the record into a permanent gap. The API draws the same line, so the
 * greyed box and the refused save agree.
 */
export function isFieldFrozen(
  line: Pick<ContainerOrderLine, "status" | "containerNumber" | "maxPayloadKg"> &
    Parameters<typeof completedStepCount>[0],
  key: LineLockableField,
): boolean {
  if (!(LINE_FIELDS_LOCKED_AFTER_START as readonly string[]).includes(key)) {
    return false;
  }
  if (!hasLeftFirstStop(line)) return false;

  const stored = line[FIELD_OF[key] as "containerNumber" | "maxPayloadKg"];
  return stored !== null && stored !== undefined && String(stored).trim() !== "";
}
